import { ButtonIcon } from '../../../../../../components/inputs/button-icon/button-icon';
import IconInfo from '../../../../../../components/icons/icon-info/icon-info';
import { Tooltip } from '../../../../../../components/tooltip/tooltip';
import { TOOLTIP_PLACEMENTS } from '../../../../../../components/tooltip/tooltip-box';

import TooltipContent from './tooltip-content';
import FeatureActions from './feature-actions';
import QuotaSection from './quota-section';

interface IFeatureQuota {
    limit: string;
    price_per_excess_usage: number;
}

interface IFeature {
    code: number;
    name: string;
    internal_name: string;
    description: string;
    has_quota: boolean;
    is_usage_based: boolean;
    is_beta: boolean;
    is_activatable?: boolean;
    quota?: IFeatureQuota;
    knowledge_base_url?: string;
}

interface IAvailableFeature {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    source: string | null;
    is_beta: boolean;
    has_quota?: boolean;
    is_activatable?: boolean;
    knowledge_base_url?: string;
}

interface IFeatureCardProps {
    feature: IFeature | IAvailableFeature;
    featureSource?: string;
    isEditing: boolean;
    quotaValue: string;
    onStartEditing: (featureId: number, currentQuota?: string) => void;
    onCancelEditing: (featureId: number) => void;
    onApplyQuota: (featureId: number) => void;
    onQuotaChange: (featureId: number, value: any) => void;
    onSetQuota?: (featureId: number, quota: number) => void;
    onActivateFeature?: (featureId: number) => void;
    onDeactivateFeature?: (featureId: number) => void;
    isActivating: boolean;
    activatingFeatureId: number | null;
    isDeactivating: boolean;
    deactivatingFeatureId: number | null;
    isSettingQuota: boolean;
    settingQuotaFeatureId: number | null;
    className?: string;
}

const FeatureCard: React.FC<IFeatureCardProps> = ({
    feature,
    featureSource,
    isEditing,
    quotaValue,
    onStartEditing,
    onCancelEditing,
    onApplyQuota,
    onQuotaChange,
    onSetQuota,
    onActivateFeature,
    onDeactivateFeature,
    isActivating,
    activatingFeatureId,
    isDeactivating,
    deactivatingFeatureId,
    isSettingQuota,
    settingQuotaFeatureId,
    className = ''
}) => {
    const featureId = 'code' in feature ? feature.code : feature.id;
    const hasQuota = 'has_quota' in feature ? feature.has_quota : false;
    const quota = 'quota' in feature ? feature.quota?.limit : null;
    const isActive = 'is_active' in feature ? feature.is_active : true; // Active features don't have is_active property
    const isActivatable = feature.is_activatable !== false; // Default to true if not specified
    const isSettingQuotaForThis = isSettingQuota && settingQuotaFeatureId === featureId;

    return (
        <div className={`feature-chip ${className} ${!isActivatable ? 'non-activatable' : ''}`}>
            {feature.knowledge_base_url && (
                <div className="knowledge-base-badge">
                    <Tooltip 
                        content={<TooltipContent content="View documentation" />} 
                        placement={TOOLTIP_PLACEMENTS.TOP}
                    >
                        <ButtonIcon
                            Component={IconInfo}
                            onClick={() => window.open(feature.knowledge_base_url, '_blank', 'noopener,noreferrer')}
                        />
                    </Tooltip>
                </div>
            )}
            <div className="feature-content">
                <span className="feature-name">{feature.name}</span>
                <span className="feature-description">{feature.description}</span>
            </div>
            <div className="feature-bottom">
                <QuotaSection
                    featureId={featureId}
                    hasQuota={hasQuota || false}
                    quota={quota}
                    isEditing={isEditing}
                    quotaValue={quotaValue}
                    onStartEditing={onStartEditing}
                    onCancelEditing={onCancelEditing}
                    onApplyQuota={onApplyQuota}
                    onQuotaChange={onQuotaChange}
                    onSetQuota={onSetQuota}
                    isSettingQuota={isSettingQuotaForThis}
                />
                <div className="feature-bottom-row">
                    <div className="feature-source-container">
                        {featureSource && (
                            <span className={`feature-source ${featureSource === 'Plan + Direct' ? 'plan-plus-direct' : ''}`}>
                                {featureSource}
                            </span>
                        )}
                        {'is_active' in feature && !feature.is_active && feature.is_beta && (
                            <span className="beta-badge">Beta</span>
                        )}
                    </div>
                    <FeatureActions
                        featureId={featureId}
                        isBeta={feature.is_beta}
                        isActive={isActive}
                        hasQuota={hasQuota || false}
                        isActivatable={isActivatable}
                        source={featureSource}
                        onActivateFeature={onActivateFeature}
                        onDeactivateFeature={onDeactivateFeature}
                        isActivating={isActivating}
                        activatingFeatureId={activatingFeatureId}
                        isDeactivating={isDeactivating}
                        deactivatingFeatureId={deactivatingFeatureId}
                    />
                </div>
            </div>
        </div>
    );
};

export default FeatureCard;
